import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getAllMerchantTags,
  getEvents,
  getEvent,
  createEvent,
  updateEvent,
  archiveEvent,
  getEventAudience,
  getEventSessions,
  createEventSession,
  updateSession,
  archiveSession,
  replaceSessionStores,
  addClientExclusion,
  removeClientExclusion,
  getClientLastMessage,
  getClientActivitiesFromDate,
  getAssociateById,
  getEventSalesAttribution,
} from '../../services/events';

// ============================================
// QUERY HOOKS
// ============================================

/**
 * Hook to fetch merchant-level tags for audience targeting
 * @param {Object} options
 * @param {Object} options.query - Query params (offset, limit, archived)
 * @param {Object} options.useQueryOptions - Additional react-query options
 */
export function useMerchantTags({ query = {}, useQueryOptions = {} } = {}) {
  return useQuery(['merchant-tags', query], async () => getAllMerchantTags({ query, loadingStatusOff: true }), {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    ...useQueryOptions,
  });
}

/**
 * Hook to fetch paginated list of events
 * @param {Object} options
 * @param {Object} options.query - Query params (page, limit, status, etc.)
 * @param {Object} options.useQueryOptions - Additional react-query options
 */
export function useEvents({ query = {}, useQueryOptions = {} } = {}) {
  return useQuery(['events', query], async () => getEvents({ query, loadingStatusOff: true }), {
    refetchOnWindowFocus: false,
    ...useQueryOptions,
  });
}

/**
 * Hook to fetch a single event by ID
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {Object} options.useQueryOptions - Additional react-query options
 */
export function useEvent({ eventId, useQueryOptions = {} } = {}) {
  return useQuery(['event', eventId], async () => getEvent({ eventId, loadingStatusOff: true }), {
    enabled: !!eventId,
    refetchOnWindowFocus: false,
    ...useQueryOptions,
  });
}

/**
 * Hook to fetch computed audience for an event (paginated)
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {Object} options.query - Query params (page, limit, search, etc.)
 * @param {Object} options.useQueryOptions - Additional react-query options
 */
export function useEventAudience({ eventId, query = {}, useQueryOptions = {} } = {}) {
  return useQuery(
    ['event-audience', eventId, query],
    async () => getEventAudience({ eventId, query, loadingStatusOff: true }),
    {
      enabled: !!eventId,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      ...useQueryOptions,
    },
  );
}

/**
 * Hook to fetch sessions for an event
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {Object} options.useQueryOptions - Additional react-query options
 */
export function useEventSessions({ eventId, useQueryOptions = {} } = {}) {
  return useQuery(['event-sessions', eventId], async () => getEventSessions({ eventId, loadingStatusOff: true }), {
    enabled: !!eventId,
    refetchOnWindowFocus: false,
    ...useQueryOptions,
  });
}

// ============================================
// MUTATION HOOKS
// ============================================

/**
 * Hook to create a new event
 * Invalidates events list on success
 */
export function useCreateEvent() {
  const queryClient = useQueryClient();
  return useMutation(data => createEvent({ data, loadingStatusOff: true }), {
    onSuccess: () => {
      queryClient.invalidateQueries(['events']);
    },
  });
}

/**
 * Hook to update an existing event
 * Invalidates events list and single event on success
 */
export function useUpdateEvent() {
  const queryClient = useQueryClient();
  return useMutation(({ eventId, data }) => updateEvent({ eventId, data, loadingStatusOff: true }), {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['events']);
      queryClient.invalidateQueries(['event', variables.eventId]);
      queryClient.invalidateQueries(['event-audience', variables.eventId]);
    },
  });
}

/**
 * Hook to archive (soft delete) an event
 * Invalidates events list on success
 */
export function useArchiveEvent() {
  const queryClient = useQueryClient();
  return useMutation(eventId => archiveEvent({ eventId, loadingStatusOff: true }), {
    onSuccess: () => {
      queryClient.invalidateQueries(['events']);
    },
  });
}

/**
 * Hook to create a new session for an event
 * Invalidates event sessions and event details on success
 */
export function useCreateSession() {
  const queryClient = useQueryClient();
  return useMutation(({ eventId, data }) => createEventSession({ eventId, data, loadingStatusOff: true }), {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['event-sessions', variables.eventId]);
      queryClient.invalidateQueries(['event', variables.eventId]);
    },
  });
}

/**
 * Hook to update an existing session
 * Invalidates event sessions on success
 */
export function useUpdateSession() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ eventId, sessionId, data }) => updateSession({ eventId, sessionId, data, loadingStatusOff: true }),
    {
      onSuccess: (_, variables) => {
        if (variables.eventId) {
          queryClient.invalidateQueries(['event-sessions', variables.eventId]);
        }
      },
    },
  );
}

/**
 * Hook to archive (soft delete) a session
 * Invalidates event sessions on success
 */
export function useArchiveSession() {
  const queryClient = useQueryClient();
  return useMutation(({ eventId, sessionId }) => archiveSession({ eventId, sessionId, loadingStatusOff: true }), {
    onSuccess: (_, variables) => {
      if (variables.eventId) {
        queryClient.invalidateQueries(['event-sessions', variables.eventId]);
        queryClient.invalidateQueries(['event', variables.eventId]);
      }
    },
  });
}

/**
 * Hook to replace all stores for a session
 * Invalidates session stores on success
 */
export function useReplaceSessionStores() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ eventId, sessionId, storeIds }) =>
      replaceSessionStores({ eventId, sessionId, storeIds, loadingStatusOff: true }),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['session-stores', variables.sessionId]);
      },
    },
  );
}

/**
 * Hook to exclude a client from an event audience.
 * Calls PUT /merchants/events/:eventId/audience/exclusions/:clientId
 * Invalidates event and audience queries on success.
 */
export function useExcludeClient() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ eventId, clientId }) => addClientExclusion({ eventId, clientId, loadingStatusOff: true }),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['event', variables.eventId]);
        queryClient.invalidateQueries(['event-audience', variables.eventId]);
      },
    },
  );
}

/**
 * Hook to restore a client to an event audience (remove exclusion).
 * Calls DELETE /merchants/events/:eventId/audience/exclusions/:clientId
 * Invalidates event and audience queries on success.
 */
export function useRestoreClient() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ eventId, clientId }) => removeClientExclusion({ eventId, clientId, loadingStatusOff: true }),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['event', variables.eventId]);
        queryClient.invalidateQueries(['event-audience', variables.eventId]);
      },
    },
  );
}

// ============================================
// CLIENT DATA HOOKS (for expanded rows)
// ============================================

/**
 * Hook to fetch the last outbound message sent to a client
 * @param {Object} options
 * @param {string|number} options.clientId - Client ID
 * @param {boolean} options.enabled - Whether to enable the query
 * @param {Object} options.useQueryOptions - Additional react-query options
 */
export function useClientLastMessage({ clientId, enabled = true, useQueryOptions = {} } = {}) {
  return useQuery(
    ['client-last-message', clientId],
    async () => getClientLastMessage({ clientId, loadingStatusOff: true }),
    {
      enabled: !!clientId && enabled,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000, // Cache for 1 minute
      ...useQueryOptions,
    },
  );
}

/**
 * Hook to fetch client activities from a specific date
 * @param {Object} options
 * @param {string|number} options.clientId - Client ID
 * @param {string} options.fromDate - ISO 8601 date to filter from
 * @param {number} options.limit - Number of activities to fetch
 * @param {boolean} options.enabled - Whether to enable the query
 * @param {Object} options.useQueryOptions - Additional react-query options
 */
export function useClientActivitiesFromDate({
  clientId,
  fromDate,
  limit = 5,
  enabled = true,
  useQueryOptions = {},
} = {}) {
  return useQuery(
    ['client-activities-from-date', clientId, fromDate],
    async () => getClientActivitiesFromDate({ clientId, fromDate, limit, loadingStatusOff: true }),
    {
      enabled: !!clientId && !!fromDate && enabled,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000, // Cache for 1 minute
      ...useQueryOptions,
    },
  );
}

/**
 * Hook to fetch associate by ID
 * @param {Object} options
 * @param {string|number} options.associateId - Associate ID
 * @param {boolean} options.enabled - Whether to enable the query
 * @param {Object} options.useQueryOptions - Additional react-query options
 */
export function useAssociate({ associateId, enabled = true, useQueryOptions = {} } = {}) {
  return useQuery(['associate', associateId], async () => getAssociateById({ associateId, loadingStatusOff: true }), {
    enabled: !!associateId && enabled,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes (associate data rarely changes)
    ...useQueryOptions,
  });
}

/**
 * Hook to fetch sales attribution for an event
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {Object} options.useQueryOptions - Additional react-query options
 */
export function useEventSalesAttribution({ eventId, useQueryOptions = {} } = {}) {
  return useQuery(
    ['event-sales-attribution', eventId],
    async () => getEventSalesAttribution({ eventId, loadingStatusOff: true }),
    {
      enabled: !!eventId,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000, // Cache for 1 minute
      retry: false,
      ...useQueryOptions,
    },
  );
}
